import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { contentAPI, contentCalendarAPI } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import type { Content } from '../../types'
import PostYourselfDialog from '../PostYourselfDialog'
import { clipFactoryAPI, clipFactoryErrorText, type PostCopy } from './clipFactoryApi'
import { formatTime, type Range } from './clipUtils'

type Platform = 'tiktok' | 'instagram' | 'youtube' | 'twitter' | 'linkedin'

const PLATFORMS: { id: Platform; label: string }[] = [
  { id: 'tiktok', label: 'TikTok' },
  { id: 'instagram', label: 'Instagram Reels' },
  { id: 'youtube', label: 'YouTube Shorts' },
  { id: 'twitter', label: 'X' },
  { id: 'linkedin', label: 'LinkedIn' },
]

/** Where FinCreator's own accounts can be posted a video on a schedule (services/publisher). */
const SCHEDULABLE: Partial<Record<Platform, string>> = { youtube: 'YouTube', tiktok: 'TikTok' }

/** The clip a Library draft is written for. */
export interface PostTarget {
  videoId: string
  filename: string
  clipKey: string
  title: string
  range: Range
}

interface PostPanelProps {
  target: PostTarget
  postCopy: PostCopy | undefined
  /** The finished export's signed download link; null until there is one. */
  downloadUrl: string | null
  /** The finished export for these settings (scheduling posts it); null until there is one. */
  exportId?: string | null
  defaultPlatform: Platform
}

/**
 * Posting: "Send to Library" saves a draft post with the clip's caption,
 * hashtags and title, linked back to the clip (before or after it's
 * exported). There's no posting service for TikTok, Instagram or
 * YouTube yet, so the clip itself is downloaded and posted by hand. Then
 * "Post it yourself" gives the checked caption and the upload page, and
 * "I posted it" keeps the post's link, so its views show on the draft in
 * Library like any other post.
 */
const PostPanel = ({ target, postCopy, downloadUrl, exportId = null, defaultPlatform }: PostPanelProps) => {
  const isAdmin = useAuthStore((state) => !!state.user?.is_superuser)
  const [when, setWhen] = useState('')
  const [scheduling, setScheduling] = useState(false)
  const [scheduled, setScheduled] = useState<string | null>(null)
  const [scheduleError, setScheduleError] = useState<string | null>(null)
  const [platform, setPlatform] = useState<Platform>(defaultPlatform)
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')
  // The Library draft just saved for this clip, and whether its link was posted.
  const [draft, setDraft] = useState<Content | null>(null)
  const [posting, setPosting] = useState(false)
  const [posted, setPosted] = useState(false)

  useEffect(() => setPlatform(defaultPlatform), [defaultPlatform])
  useEffect(() => {
    setState('idle')
    setDraft(null)
    setPosted(false)
    setScheduled(null)
    setScheduleError(null)
  }, [target.videoId, target.clipKey])

  // An admin can schedule the draft to FinCreator's own YouTube or TikTok:
  // the publisher fetches the MP4 then, from a link that opens this export
  // only and keeps it on the server until the post has gone out.
  const schedule = async () => {
    if (!draft || !exportId) return
    const at = new Date(when)
    if (Number.isNaN(at.getTime()) || at.getTime() <= Date.now()) {
      setScheduleError('Pick a time in the future.')
      return
    }
    setScheduling(true)
    setScheduleError(null)
    try {
      const link = await clipFactoryAPI.publishLink(target.videoId, exportId, Math.floor(at.getTime() / 1000))
      await contentAPI.update(draft.id, { media_urls: [link.url] })
      await contentCalendarAPI.schedule({
        content_type: 'POST',
        content_id: draft.id,
        title: draft.title || target.title,
        body: draft.text,
        scheduled_at: at.toISOString(),
        tags: draft.hashtags || [],
      })
      setScheduled(at.toLocaleString())
    } catch (e) {
      setScheduleError(clipFactoryErrorText(e, "Couldn't schedule the post."))
    } finally {
      setScheduling(false)
    }
  }
  const network = SCHEDULABLE[draft?.content_type as Platform]

  const send = async () => {
    setState('saving')
    // The draft carries where it came from, so Library links back to the clip
    // in the Clips tab. That's also where a fresh MP4 link is made: the export's
    // download link is signed and expires (MEDIA_LINK_MINUTES, an hour by
    // default), and a draft can be saved before there is an export at all.
    try {
      const created = await contentAPI.create({
        content_type: platform,
        title: postCopy?.titles[0] || target.title,
        text: postCopy?.caption || target.title,
        hashtags: (postCopy?.hashtags || []).map((tag) => tag.replace(/^#/, '')),
        ...(downloadUrl ? { media_urls: [downloadUrl] } : {}),
        description: `Clip from ${target.filename} (${formatTime(target.range.start)}–${formatTime(target.range.end)}).`,
        source: {
          kind: 'clip',
          video_id: target.videoId,
          filename: target.filename,
          start: target.range.start,
          end: target.range.end,
        },
      })
      setDraft(created?.id ? created : null)
      setPosted(false)
      setState('saved')
    } catch {
      setState('failed')
    }
  }

  return (
    <div className="mt-2 space-y-2 border-t border-gray-200 pt-3" aria-label="Post">
      <label className="block text-sm text-gray-700">
        Platform
        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value as Platform)}
          className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
        >
          {PLATFORMS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        onClick={send}
        disabled={state === 'saving'}
        className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
      >
        {state === 'saving' ? 'Saving…' : 'Send to Library'}
      </button>
      {state === 'saved' && (
        <p className="text-xs text-success-600">
          Saved as a draft in <Link to="/content" className="underline">Library</Link>.
        </p>
      )}
      {state === 'saved' && draft && !posted && (
        <button
          type="button"
          onClick={() => setPosting(true)}
          className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          Posted it? Save the link
        </button>
      )}
      {posted && (
        <p className="text-xs text-success-600">
          Link saved. Its views show on the draft in <Link to="/content" className="underline">Library</Link>.
        </p>
      )}
      {posting && draft && (
        <PostYourselfDialog
          content={draft}
          withImage={false}
          onClose={() => setPosting(false)}
          onPosted={() => {
            setPosting(false)
            setPosted(true)
          }}
        />
      )}
      {isAdmin && state === 'saved' && draft && network && (
        <div className="space-y-1 rounded-md border border-gray-200 p-2" data-testid="schedule-clip">
          {scheduled ? (
            <p className="text-xs text-success-600">
              Scheduled on FinCreator&rsquo;s {network} for {scheduled}. It&rsquo;s on the{' '}
              <Link to="/scheduled" className="underline">
                Scheduled
              </Link>{' '}
              page.
            </p>
          ) : exportId ? (
            <>
              <label className="block text-xs font-medium text-gray-700">
                Post on FinCreator&rsquo;s {network} at
                <input
                  type="datetime-local"
                  value={when}
                  onChange={(e) => setWhen(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                />
              </label>
              <button
                type="button"
                onClick={schedule}
                disabled={scheduling || !when}
                className="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {scheduling ? 'Scheduling…' : `Schedule on ${network}`}
              </button>
              {scheduleError && <p role="alert" className="text-xs text-danger-600">{scheduleError}</p>}
            </>
          ) : (
            <p className="text-xs text-gray-500">Export the clip to schedule it on FinCreator&rsquo;s {network}.</p>
          )}
        </div>
      )}
      {state === 'failed' && <p role="alert" className="text-xs text-danger-600">Couldn't save the draft. Try again.</p>}
      {downloadUrl ? (
        <a
          href={downloadUrl}
          download
          className="block rounded-md border border-gray-300 px-4 py-2 text-center text-sm text-gray-700 hover:bg-gray-100"
        >
          Download and post
        </a>
      ) : (
        <p className="text-xs text-gray-500">Export the clip to download and post it, or send it to Library.</p>
      )}
      <p className="text-xs text-gray-500">
        Posting to your own TikTok, Instagram and YouTube isn&rsquo;t connected yet: download the MP4 and post it from the app.
      </p>
    </div>
  )
}

export default PostPanel
